import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  FileAddOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";
import { useTranslation } from "react-i18next";
import {
  FIRESTORE_PRODUCT_DRAFTS_TABLE,
  FIRESTORE_PURCHASE_ORDERS_TABLE,
} from "../../../../constants/FirestoreConstant";
import {
  PRODUCT_DRAFT_STATUS_NEEDS_SUPPLIER_INPUT,
  PURCHASE_ORDER_STATUS_DRAFT,
  PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION,
} from "../../../../constants/OrderConstant";
import { addLogEntry } from "../../../../utils/logging";
import { useSuppliers } from "../../../../data/hooks/useSuppliers";
import { usePurchaseOrderShipments } from "../../../../data/hooks";
import { useSalesAggregates } from "../../../../data/hooks/useSalesAggregates";
import { compressImage } from "../../../../functions/images";
import urls from "../../../../urls";
import {
  DetailLayout,
  KeyValueList,
  Num,
  PageHeader,
  SectionCard,
  StickyActionBar,
} from "../../../../components/shared-components/purchase";
import { formatPriceByCurrency, getFormattedProductColor } from "../../../../functions/formatters";
import DiscontinuedMark from "../../../../components/shared-components/DiscontinuedMark";
import { getProductQuantityInOrders, getShippedQuantityInOrders } from "../../../../utils/stockCalculations";
import { getLineStockPicture } from "../../../../functions/lineStockPicture";
import LineStockPicture, { LineStockLegend } from "./LineStockPicture";
import { buildLinesForSelection, fillLineFromCatalog } from "../../../../functions/purchaseOrderDraftLines";

/**
 * Inkooporder aanmaken en een concept bijwerken — als eigen pagina.
 *
 * Stond hiervoor in een modal van 1200px op het inkooporderoverzicht, met
 * binnenin nóg een scrollgebied. Uit het ontwerp ("Inkooporder aanmaken"):
 * twee stappen met een eigen URL, een vaste samenvatting in de zijkolom en de
 * acties in de balk onderaan. Dat is dezelfde balk als op de detailpagina's,
 * dus "Opslaan" zit overal op dezelfde plek.
 *
 * Twee stappen, en de grens ertussen is hard:
 *   1. WELKE producten — leverancier, verkoopperiode en het aanvinken zelf.
 *   2. HOEVEEL en VOOR HOEVEEL — aantallen, verpakking en prijs per regel.
 * "Product toevoegen" in stap 2 stuurt je dus terug naar stap 1 in plaats van
 * een lege regel bij te prikken. Daardoor kan een product nooit twee keer in de
 * order staan en bestaat er geen half ingevulde regel zonder product.
 *
 * De regels van stap 2 zijn één tabel geworden; voorheen was het een
 * accordeon waarin elke regel een formulier van twee rijen opende, zodat je
 * aantallen nooit naast elkaar kon vergelijken. Wat per regel zelden nodig is
 * (prioriteit, foto's, opmerking) zit achter de uitklap van die rij.
 *
 * Twee dingen bewust NIET overgenomen uit het ontwerp:
 * - De kaart "Container" in de zijkolom. Die rekent met een volume per product,
 *   en dat bestaat hier niet: `cubicMeters` staat op de order als geheel en is
 *   iets dat de leverancier ná het versturen aanlevert (zie
 *   `getMissingSupplierFields` op de detailpagina). Een geschat volume uit de
 *   productafmetingen zou een verzonnen getal zijn op een scherm waarop je
 *   beslissingen neemt.
 * - De "Verzonden"-kolom in de productentabel van stap 1 staat in het ontwerp
 *   niet meer als eigen kolom; dat aantal staat nu onder "In bestelling".
 *
 * De kaart "Bestelgegevens" staat in beide stappen. In stap 2 is de leverancier
 * vast (een andere leverancier is een andere productlijst, dus dat kan alleen in
 * stap 1), maar de verkoopperiode niet: die stuurt het voorraadbeeld onder elke
 * regel (`LineStockPicture`), en juist bij het invullen van aantallen wil je
 * kunnen schakelen tussen "laatste 3 maanden" en "heel vorig jaar".
 */
const PurchaseOrderCreate = () => {
  const { id: routeOrderId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [addForm] = Form.useForm();
  const [draftProductForm] = Form.useForm();
  const firestore = useFirestore();

  // Deze pagina leest de collectie, dus abonneert de pagina zelf. Stond tot nu
  // toe in layouts/app-layout (op elke CMS-pagina) -- zie docs/cms-performance.md.
  useFirestoreConnect([{ collection: FIRESTORE_PURCHASE_ORDERS_TABLE }]);
  const purchaseOrders = useSelector((state) => state.firestoreReducer.ordered.purchaseOrders);
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const categories = useSelector((state) => state.firestoreReducer.ordered.categories);
  const profile = useSelector((state) => state.firebaseReducer.profile);
  const { email } = profile;
  const { suppliers, loading: suppliersLoading } = useSuppliers();
  const { shipments, loading: shipmentsLoading } = usePurchaseOrderShipments();
  const { getProductSalesInPeriod } = useSalesAggregates({ autoFetch: false });

  // `orderId` is het concept waar we in schrijven. Bij een nieuwe order is hij
  // leeg tot de eerste keer opslaan; daarna staat hij vast. Dat is het verschil
  // met de modal: die zette na het aanmaken wel `editingOrder`, maar wiste die
  // meteen weer bij het sluiten, waardoor twee keer opslaan twee orders met
  // twee ordernummers opleverde.
  const [orderId, setOrderId] = useState(routeOrderId || null);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [draftProducts, setDraftProducts] = useState([]);
  const [salesPeriod, setSalesPeriod] = useState(null);
  const [productSalesData, setProductSalesData] = useState({});
  const [salesLoading, setSalesLoading] = useState(false);
  const [productSearchText, setProductSearchText] = useState("");
  const [fileList, setFileList] = useState([]);
  const [productAttachments, setProductAttachments] = useState({});
  const [addDraftProductModalOpen, setAddDraftProductModalOpen] = useState(false);
  // Alles wat onder de streep staat (aantallen, totalen, de tekst in de
  // actiebalk) leest uit het formulier. Zonder deze teller zou dat pas
  // bijwerken als er toevallig iets anders rendert.
  const [, setFormTick] = useState(0);
  const rerender = () => setFormTick((tick) => tick + 1);

  const editingOrder = orderId ? (purchaseOrders || []).find((order) => order.id === orderId) : null;
  const isEditing = Boolean(routeOrderId);
  const loadedRef = useRef(false);

  // Standaardperiode voor de kolom "Verkocht": 6 maanden terug, of het hele
  // lopende jaar als we al over juli heen zijn.
  const getDefaultSalesPeriod = () => {
    const now = dayjs();
    if (now.month() > 6) return [dayjs().startOf("year"), dayjs().endOf("year")];
    return [dayjs().subtract(6, "months").startOf("day"), dayjs().endOf("day")];
  };

  // Snel achter elkaar een andere periode kiezen mag niet eindigen met de
  // cijfers van de vorige keuze, omdat dat antwoord toevallig later binnenkwam.
  const salesRequestRef = useRef(0);
  const loadSalesData = async (period) => {
    if (!period || !period[0] || !period[1]) return;
    const request = ++salesRequestRef.current;
    setSalesLoading(true);
    try {
      const salesData = await getProductSalesInPeriod(period[0].unix(), period[1].unix());
      if (request === salesRequestRef.current) setProductSalesData(salesData || {});
    } finally {
      if (request === salesRequestRef.current) setSalesLoading(false);
    }
  };

  const loadDraftOrder = (draftOrder) => {
    const regularProducts = (draftOrder.products || []).filter((p) => p.productId);
    const draftProductsFromOrder = (draftOrder.products || []).filter((p) => !p.productId && p.productDraftId);

    addForm.setFieldsValue({
      supplier: draftOrder.supplierId,
      exchangeRate: draftOrder.exchangeRate,
      comments: draftOrder.comments || "",
      products: [
        // Lege prijs of verpakking aanvullen uit de catalogus, net als bij een
        // nieuwe regel in stap 2. Een concept dat in stap 1 is opgeslagen, heeft
        // die velden nog niet (zie `purchaseOrderDraftLines.js`).
        ...regularProducts.map((product) =>
          fillLineFromCatalog(
            {
              product: product.productId,
              amount: product.amount,
              innerPackagingQuantity: product.innerPackagingQuantity,
              masterPackagingQuantity: product.masterPackagingQuantity,
              price: product.price,
              priority: product.priority || false,
            },
            (products || []).find((p) => p.id === product.productId),
          ),
        ),
        ...draftProductsFromOrder.map((product) => ({
          product: null,
          isDraft: true,
          draftTempId: product.productDraftId,
          draftProductName: product.productName,
          draftProductCode: product.productCode,
          draftProductBarcode: product.productBarcode,
          amount: product.amount || 0,
          price: product.price || 0,
          priority: product.priority || false,
        })),
      ],
    });

    setSelectedProducts(regularProducts.map((product) => product.productId).filter(Boolean));
    setDraftProducts(
      draftProductsFromOrder.map((p) => ({
        tempId: p.productDraftId,
        productName: p.productName,
        productCode: p.productCode,
        productBarcode: p.productBarcode,
        isDraft: true,
      })),
    );
    setStep(regularProducts.length > 0 || draftProductsFromOrder.length > 0 ? 2 : 1);

    if (draftOrder.images?.length) {
      setFileList(
        draftOrder.images.map((url, index) => ({
          uid: `existing-${index}`,
          name: `image-${index}`,
          status: "done",
          url,
        })),
      );
    }

    const attachments = {};
    (draftOrder.products || []).forEach((product) => {
      if (product.comment || product.images?.length) {
        attachments[product.productId] = {
          hasAttachments: true,
          comment: product.comment || "",
          fileList: (product.images || []).map((url, index) => ({
            uid: `existing-${product.productId}-${index}`,
            name: `image-${index}`,
            status: "done",
            url,
          })),
        };
      }
    });
    setProductAttachments(attachments);
  };

  // Verkoopcijfers ophalen zodra de pagina opent, en bij bewerken het concept
  // inladen zodra de orders binnen zijn.
  useEffect(() => {
    const period = getDefaultSalesPeriod();
    setSalesPeriod(period);
    loadSalesData(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wacht ook op de catalogus: zonder catalogus is er niets om lege regels mee
  // aan te vullen, en dit draait maar één keer.
  useEffect(() => {
    if (!routeOrderId || loadedRef.current || !purchaseOrders || !products) return;
    const order = purchaseOrders.find((o) => o.id === routeOrderId);
    if (!order) return;
    loadedRef.current = true;
    loadDraftOrder(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeOrderId, purchaseOrders, products]);

  const selectedSupplierId = addForm.getFieldValue("supplier");
  const selectedSupplier = (suppliers || []).find((s) => s.id === selectedSupplierId);
  const currency = selectedSupplier?.currency || "USD";

  const getCategoryName = (categoryId) => {
    if (!categoryId || !categories) return t("uncategorized");
    const category = categories.find(
      (cat) => cat.categoryId === categoryId || cat.subcategories?.some((sub) => sub.subcategoryId === categoryId),
    );
    if (!category) return categoryId;
    if (category.categoryId === categoryId) return category.name;
    const subcategory = category.subcategories?.find((sub) => sub.subcategoryId === categoryId);
    return subcategory ? `${category.name} > ${subcategory.name}` : categoryId;
  };

  // Uploaden van nieuw gekozen afbeeldingen. In de modal gebeurde dit niet: de
  // bestanden werden wel gecomprimeerd en getoond, maar bij opslaan bleven
  // alleen de al bestaande URL's over ("new files will be uploaded when
  // finalizing" — die stap bestaat in deze flow niet). Wat je uploadde was dus
  // stilletjes weg zodra je opsloeg.
  const uploadNewImages = async (files, pathPrefix) => {
    const storage = getStorage();
    const urls = [];
    for (const file of files || []) {
      if (file.url) {
        urls.push(file.url);
        continue;
      }
      if (!file.originFileObj) continue;
      const fileRef = storageRef(storage, `${pathPrefix}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file.originFileObj);
      urls.push(await getDownloadURL(fileRef));
    }
    return urls;
  };

  const createProductDraftsForOrder = async (orderProducts, purchaseOrderId, supplierId) => {
    const updatedProducts = [];

    for (let i = 0; i < orderProducts.length; i++) {
      const product = orderProducts[i];
      // Bestaande concepten hebben een Firestore-id, nieuwe een tijdelijke
      // "draft-<timestamp>".
      const isNewDraft = product.isDraft && product.productDraftId?.startsWith("draft-");
      const isExistingDraft = product.isDraft && product.productDraftId && !product.productDraftId.startsWith("draft-");
      const { isDraft, draftTempId, draftProductName, draftProductCode, draftProductBarcode, ...cleanProduct } =
        product;

      if (isNewDraft) {
        const draftRef = await firestore.collection(FIRESTORE_PRODUCT_DRAFTS_TABLE).add({
          purchaseOrderId,
          purchaseOrderProductIndex: i,
          supplierId,
          productName: product.productName,
          productCode: product.productCode,
          productBarcode: product.productBarcode || null,
          draftProduct: {},
          status: PRODUCT_DRAFT_STATUS_NEEDS_SUPPLIER_INPUT,
          editableFields: [
            "purchasePrice",
            "innerPackagingQuantity",
            "masterPackagingQuantity",
            "length",
            "width",
            "height",
            "diameter",
            "color",
            "supplierMetadata",
          ],
          createdAt: dayjs().unix(),
          createdBy: email,
        });
        updatedProducts.push({ ...cleanProduct, productDraftId: draftRef.id });
      } else if (isExistingDraft) {
        updatedProducts.push({ ...cleanProduct, productDraftId: product.productDraftId });
      } else {
        updatedProducts.push(cleanProduct);
      }
    }

    return updatedProducts;
  };

  // Prijs en verpakkingsaantallen terugschrijven naar de catalogus.
  // De guards op null/undefined zijn er omdat "Opslaan" in stap 1 anders
  // `purchasePrice: 0` en lege verpakkingsaantallen naar élk aangevinkt product
  // schreef: in stap 1 zijn die velden nog niet ingevuld, maar ze gingen wel als
  // 0/null mee en weken dus af van de bestaande waarde.
  const updateProductPricesAndPackaging = async (orderProducts) => {
    await Promise.all(
      orderProducts.map(async (orderProduct) => {
        const product = (products || []).find((p) => p.id === orderProduct.productId);
        if (!product) return;

        const updates = {};
        if (orderProduct.price != null && orderProduct.price !== product.purchasePrice) {
          updates.purchasePrice = orderProduct.price;
        }
        if (
          orderProduct.innerPackagingQuantity != null &&
          orderProduct.innerPackagingQuantity !== product.innerPackagingQuantity
        ) {
          updates.innerPackagingQuantity = orderProduct.innerPackagingQuantity;
        }
        if (
          orderProduct.masterPackagingQuantity != null &&
          orderProduct.masterPackagingQuantity !== product.masterPackagingQuantity
        ) {
          updates.masterPackagingQuantity = orderProduct.masterPackagingQuantity;
        }
        if (Object.keys(updates).length === 0) return;

        updates.updatedAt = dayjs().unix();
        updates.updatedBy = email;
        await firestore.collection("products").doc(orderProduct.productId).update(updates);
      }),
    );
  };

  const buildOrderProducts = async (targetOrderId) => {
    // `getFieldsValue()` zónder argument geeft alleen de velden terug die op dat
    // moment gemount zijn. De wizard rendert stap 1 en stap 2 in elkaar
    // uitsluitende takken, dus vanaf stap 2 ontbrak `supplier` (dat veld staat op
    // stap 1) en werd er `supplierId: null` weggeschreven — ook bij het opslaan
    // van een bestaande order, die met producten meteen op stap 2 opent. Hetzelfde
    // gold voor `comments` bij opslaan vanaf stap 1, en voor `exchangeRate`, dat
    // wél in de store wordt gezet maar nergens een eigen veld heeft.
    // `true` geeft de hele store, inclusief wat nu niet in beeld staat.
    const values = addForm.getFieldsValue(true);
    // In stap 1 is de selectie leidend, niet de regels van stap 2: dezelfde
    // regels die "Volgende" zou maken, inclusief prijs en verpakking uit de
    // catalogus. Voorheen viel opslaan in stap 1 terug op kale product-id's
    // (prijs 0, geen verpakking) zolang stap 2 nog niet bezocht was. Was stap 2
    // wél bezocht, dan gingen in stap 1 nieuw aangevinkte producten verloren.
    const productSources =
      step === 1
        ? buildLinesForSelection({
            existingLines: values.products,
            selectedProductIds: selectedProducts,
            draftProducts,
            products,
          })
        : values.products || [];

    const regularSources = productSources.filter((p) => p?.product && !p?.isDraft);
    const draftSources = productSources.filter((p) => p?.isDraft);

    const orderProducts = [];
    for (const source of regularSources) {
      const productData = (products || []).find((p) => p.id === source.product);

      let productDimensions = null;
      if (productData) {
        if (productData.length && productData.width && productData.height) {
          productDimensions = {
            length: productData.length,
            width: productData.width,
            height: productData.height,
          };
        } else if (productData.height && productData.diameter) {
          productDimensions = { height: productData.height, diameter: productData.diameter };
        } else if (productData.length && productData.width) {
          productDimensions = { length: productData.length, width: productData.width };
        }
      }

      const baseProduct = {
        productId: source.product,
        amount: source.amount || 0,
        productName: productData?.name || null,
        productColor: productData?.color || null,
        productCode: productData?.code || null,
        productBarcode: productData?.barcode || null,
        productImages: productData?.image || [],
        productDimensions,
        innerPackagingQuantity: source.innerPackagingQuantity || null,
        masterPackagingQuantity: source.masterPackagingQuantity || null,
        price: source.price || 0,
        priority: source.priority || false,
        supplierMetadata: productData?.supplierMetadata || null,
      };

      const attachments = productAttachments[source.product];
      if (attachments?.hasAttachments) {
        if (attachments.comment) baseProduct.comment = attachments.comment;
        const urls = await uploadNewImages(attachments.fileList, `purchase-orders/${targetOrderId}/${source.product}`);
        if (urls.length > 0) baseProduct.images = urls;
      }

      orderProducts.push(baseProduct);
    }

    const draftOrderProducts =
      draftSources.length > 0
        ? draftSources.map((draft) => ({
            productId: null,
            productDraftId: draft.draftTempId || `draft-${Date.now()}-${Math.random()}`,
            amount: draft.amount || 0,
            productName: draft.draftProductName,
            productCode: draft.draftProductCode,
            productBarcode: draft.draftProductBarcode || null,
            productImages: [],
            productDimensions: null,
            innerPackagingQuantity: null,
            masterPackagingQuantity: null,
            price: draft.price || 0,
            priority: draft.priority || false,
            supplierMetadata: null,
            isDraft: true,
          }))
        : draftProducts.map((draft) => ({
            productId: null,
            productDraftId: draft.tempId,
            amount: 0,
            productName: draft.productName,
            productCode: draft.productCode,
            productBarcode: draft.productBarcode,
            productImages: [],
            productDimensions: null,
            innerPackagingQuantity: null,
            masterPackagingQuantity: null,
            price: 0,
            priority: false,
            supplierMetadata: null,
            isDraft: true,
          }));

    return { values, orderProducts, allOrderProducts: [...orderProducts, ...draftOrderProducts] };
  };

  /**
   * Opslaan. `sendToSupplier` zet de order daarna meteen op "wacht op
   * bevestiging" — dezelfde overgang als de knop "Zet op besteld" in het
   * overzicht, inclusief `orderedAt`.
   *
   * Het document wordt eerst aangemaakt met `.doc()` (dat geeft een id zónder
   * te schrijven). Daardoor is het ordernummer al bekend vóór de schrijfactie,
   * kunnen afbeeldingen en productconcepten er meteen aan hangen en is één
   * `set()` genoeg in plaats van toevoegen-en-daarna-bijwerken.
   */
  const handleSave = async ({ sendToSupplier = false } = {}) => {
    // Zonder leverancier is de order onbruikbaar: de producten hangen eraan en
    // er is niemand om hem naartoe te sturen. Het formulier markeerde het veld
    // al als verplicht, maar opslaan liep buiten de validatie om.
    if (!addForm.getFieldValue("supplier")) {
      messageApi.error(t("please_select_supplier"));
      return;
    }

    try {
      setIsSubmitting(true);

      const docRef = orderId
        ? firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(orderId)
        : firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc();

      const { values, orderProducts, allOrderProducts } = await buildOrderProducts(docRef.id);
      const productsWithDraftIds = await createProductDraftsForOrder(allOrderProducts, docRef.id, values.supplier);

      const totalProducts = productsWithDraftIds.reduce((sum, product) => sum + (product.amount || 0), 0);
      const totalPrice = productsWithDraftIds.reduce(
        (sum, product) => sum + (product.price || 0) * (product.amount || 0),
        0,
      );
      const supplier = (suppliers || []).find((s) => s.id === values.supplier);
      const orderImages = await uploadNewImages(fileList, `purchase-orders/${docRef.id}`);

      if (orderId) {
        const updateData = {
          supplierId: values.supplier || null,
          supplierName: supplier?.name || null,
          currency: supplier?.currency || "USD",
          totalProducts,
          totalPrice,
          exchangeRate: values.exchangeRate || null,
          products: productsWithDraftIds,
          comments: values.comments || "",
          images: orderImages,
          updatedAt: dayjs().unix(),
          updatedBy: email,
          lastModifiedBy: { email, updatedAt: dayjs().unix() },
          log: addLogEntry(editingOrder?.log || [], { action: "draft_saved", user: email }),
        };

        if (sendToSupplier) {
          updateData.status = PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION;
          updateData.orderedAt = dayjs().unix();
          updateData.completedAt = null;
          updateData.log = addLogEntry(updateData.log, {
            action: "status_changed",
            user: email,
            oldValue: editingOrder?.status || PURCHASE_ORDER_STATUS_DRAFT,
            newValue: PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION,
          });
        }

        await docRef.update(updateData);
      } else {
        const configDocRef = firestore.collection("config").doc("orders");
        let newOrderNumber;
        await firestore.runTransaction(async (transaction) => {
          const configSnapshot = await transaction.get(configDocRef);
          const newCounter = (configSnapshot.data()?.currentOrderCounter || 0) + 1;
          transaction.update(configDocRef, { currentOrderCounter: newCounter });
          newOrderNumber = `IO-${dayjs().format("YYYYMMDD")}-${newCounter}`;
        });

        const log = [{ action: "draft_created", timestamp: dayjs().unix(), user: email }];
        if (sendToSupplier) {
          log.push({
            action: "status_changed",
            timestamp: dayjs().unix(),
            user: email,
            oldValue: PURCHASE_ORDER_STATUS_DRAFT,
            newValue: PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION,
          });
        }

        await docRef.set({
          orderNumber: newOrderNumber,
          supplierId: values.supplier || null,
          supplierName: supplier?.name || null,
          currency: supplier?.currency || "USD",
          totalProducts,
          totalPrice,
          exchangeRate: values.exchangeRate || null,
          status: sendToSupplier ? PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION : PURCHASE_ORDER_STATUS_DRAFT,
          orderDate: dayjs().unix(),
          ...(sendToSupplier ? { orderedAt: dayjs().unix() } : {}),
          products: productsWithDraftIds,
          comments: values.comments || "",
          images: orderImages,
          createdBy: email,
          createdAt: dayjs().unix(),
          log,
        });

        setOrderId(docRef.id);
        loadedRef.current = true;
        // De URL volgt het concept, zodat vernieuwen of terugkomen dezelfde
        // order opent in plaats van een lege wizard. Bij versturen slaan we dit
        // over; dan gaan we hieronder toch naar de detailpagina.
        if (!sendToSupplier) navigate(`${urls.purchaseOrdersList}/${docRef.id}/edit`, { replace: true });
      }

      // Alleen vanuit stap 2 terugschrijven naar de catalogus: in stap 1 zijn
      // prijs en verpakking nog niet ingevuld.
      if (step === 2) await updateProductPricesAndPackaging(orderProducts);

      if (sendToSupplier) {
        messageApi.success(t("notifications.purchase_order_status_update_successful"));
        navigate(`${urls.purchaseOrdersList}/${docRef.id}`);
      } else {
        messageApi.success(t("draft_saved_successfully"));
      }
    } catch (error) {
      console.error("Error saving purchase order:", error);
      messageApi.error(t("draft_save_failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddDraftProduct = () => {
    draftProductForm.validateFields().then((values) => {
      setDraftProducts((prev) => [
        ...prev,
        {
          tempId: `draft-${Date.now()}`,
          productName: values.productName,
          productCode: values.productCode,
          productBarcode: values.productBarcode || null,
          isDraft: true,
        },
      ]);
      draftProductForm.resetFields();
      setAddDraftProductModalOpen(false);
    });
  };

  const handleProductAttachmentToggle = (productId, checked) => {
    setProductAttachments((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        hasAttachments: checked,
        comment: checked ? prev[productId]?.comment || "" : undefined,
        fileList: checked ? prev[productId]?.fileList || [] : undefined,
      },
    }));
  };

  const compressFileList = async (newFileList) =>
    Promise.all(
      newFileList.map(async (file) => {
        if (file.originFileObj && !file.compressed) {
          return { ...file, originFileObj: await compressImage(file.originFileObj, 0.7, 1200), compressed: true };
        }
        return file;
      }),
    );

  /**
   * Producten van de leverancier, gesorteerd op verkoop en met varianten direct
   * onder elkaar. Varianten zijn in dit project symmetrisch gekoppeld
   * (`productVariants` verwijst beide kanten op), dus de groepen zijn de
   * samenhangende componenten van die graaf. Een groep krijgt de plek van zijn
   * best verkochte lid, zodat je een kleurvariant nooit twintig regels lager
   * terugvindt.
   */
  const getSupplierProducts = () => {
    if (!selectedSupplier?.productIds?.length || !products) return [];

    let supplierProducts = products.filter((p) => selectedSupplier.productIds.includes(p.id));
    if (productSearchText) {
      const search = productSearchText.toLowerCase();
      supplierProducts = supplierProducts.filter(
        (p) => p.name?.toLowerCase().includes(search) || p.code?.toLowerCase().includes(search),
      );
    }

    const soldOf = (productId) => productSalesData[productId]?.soldQuantity || 0;
    const sortedBySales = supplierProducts.slice().sort((a, b) => {
      const diff = soldOf(b.id) - soldOf(a.id);
      return diff !== 0 ? diff : (a.code || "").localeCompare(b.code || "");
    });

    const productById = new Map(sortedBySales.map((p) => [p.id, p]));
    const adjacency = new Map();
    const ensureAdj = (productId) => {
      if (!adjacency.has(productId)) adjacency.set(productId, new Set());
      return adjacency.get(productId);
    };

    sortedBySales.forEach((p) => {
      if (!p?.id) return;
      ensureAdj(p.id);
      (Array.isArray(p.productVariants) ? p.productVariants : []).forEach((variant) => {
        const variantId = variant?.productId;
        if (!variantId || variantId === p.id || !productById.has(variantId)) return;
        ensureAdj(p.id).add(variantId);
        ensureAdj(variantId).add(p.id);
      });
    });

    const visited = new Set();
    const groups = [];
    sortedBySales.forEach((p) => {
      if (!p?.id || visited.has(p.id)) return;
      const queue = [p.id];
      const componentIds = [];
      visited.add(p.id);
      while (queue.length) {
        const currentId = queue.shift();
        componentIds.push(currentId);
        (adjacency.get(currentId) || []).forEach((neighborId) => {
          if (visited.has(neighborId)) return;
          visited.add(neighborId);
          queue.push(neighborId);
        });
      }
      const items = componentIds
        .map((componentId) => productById.get(componentId))
        .filter(Boolean)
        .sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true, sensitivity: "base" }));
      groups.push({ maxSold: items.reduce((max, item) => Math.max(max, soldOf(item.id)), 0), items });
    });

    groups.sort((a, b) => {
      const diff = b.maxSold - a.maxSold;
      return diff !== 0 ? diff : (a.items[0]?.code || "").localeCompare(b.items[0]?.code || "");
    });

    return groups.flatMap((group) => group.items);
  };

  // Van stap 1 naar stap 2: voor elk aangevinkt product een regel, met de
  // prijs en verpakking uit de catalogus als startwaarde. Regels die er al
  // stonden houden hun ingevulde waarden; alleen lege velden worden aangevuld.
  const goToStep2 = () => {
    if (selectedProducts.length === 0 && draftProducts.length === 0) {
      messageApi.error(t("select_min_one_product"));
      return;
    }

    addForm.setFieldsValue({
      products: buildLinesForSelection({
        existingLines: addForm.getFieldValue("products"),
        selectedProductIds: selectedProducts,
        draftProducts,
        products,
      }),
    });
    setStep(2);
  };

  // Een regel weghalen haalt hem ook uit de selectie van stap 1. Anders staat
  // hij daar nog aangevinkt en komt hij bij de volgende keer "volgende" gewoon
  // weer terug.
  const removeLine = (field, remove) => {
    const row = addForm.getFieldValue(["products", field.name]) || {};
    remove(field.name);
    if (row.isDraft) setDraftProducts((prev) => prev.filter((d) => d.tempId !== row.draftTempId));
    else if (row.product) setSelectedProducts((prev) => prev.filter((productId) => productId !== row.product));
    rerender();
  };

  const lines = addForm.getFieldValue("products") || [];
  const totals = lines.reduce(
    (acc, row) => ({
      units: acc.units + (Number(row?.amount) || 0),
      amount: acc.amount + (Number(row?.amount) || 0) * (Number(row?.price) || 0),
    }),
    { units: 0, amount: 0 },
  );
  const chosenCount = selectedProducts.length + draftProducts.length;

  // Naar de leverancier sturen kan pas als elke regel compleet is — dezelfde
  // eis als de knop "Zet op besteld" in het overzicht. Productconcepten zijn de
  // uitzondering: daar vult de leverancier de rest juist zelf in.
  const incompleteLines = lines.filter((row) =>
    row?.isDraft
      ? !(row.amount > 0)
      : !(row?.amount > 0 && row?.innerPackagingQuantity > 0 && row?.masterPackagingQuantity > 0 && row?.price > 0),
  );
  const canSendToSupplier = lines.length > 0 && incompleteLines.length === 0 && Boolean(selectedSupplierId);

  const actionBarNote = (() => {
    if (step === 1) {
      return chosenCount ? `${chosenCount} product${chosenCount === 1 ? "" : "en"} gekozen` : "Kies minstens één product om verder te gaan.";
    }
    if (!totals.units) return "Vul aantallen in voordat je opslaat.";
    return (
      <>
        {lines.length} regel{lines.length === 1 ? "" : "s"} · <Num>{totals.units}</Num> stuks ·{" "}
        <Num>{formatPriceByCurrency(totals.amount, currency)}</Num>
        {incompleteLines.length > 0 ? (
          <span style={{ color: "#96601A" }}>
            {" · "}
            {incompleteLines.length} regel{incompleteLines.length === 1 ? "" : "s"} nog niet compleet
          </span>
        ) : null}
      </>
    );
  })();

  const confirmSendToSupplier = () => {
    Modal.confirm({
      title: "Naar leverancier sturen?",
      content: (
        <span>
          {selectedSupplier?.name || "De leverancier"} ziet de order direct in het portaal en krijgt er bericht van. Je
          kunt hem daarna nog aanpassen en opnieuw versturen.
        </span>
      ),
      okText: "Versturen",
      cancelText: t("cancel"),
      onOk: () => handleSave({ sendToSupplier: true }),
    });
  };

  const stepItems = [
    { key: 1, label: "Producten kiezen" },
    { key: 2, label: "Aantallen & prijzen" },
  ];

  const stepNav = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {stepItems.map((item, index) => (
        <React.Fragment key={item.key}>
          {index > 0 ? <span style={{ width: 22, height: 1, background: "#E1DFDA" }} /> : null}
          <Button
            type="text"
            onClick={() => (item.key === 1 ? setStep(1) : goToStep2())}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, paddingInline: 10 }}
          >
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontVariantNumeric: "tabular-nums",
                color: "#fff",
                flex: "none",
                background: step === item.key ? "#938370" : "#C9BFAF",
              }}
            >
              {item.key}
            </span>
            <span
              style={{ fontSize: 13, fontWeight: 500, color: step === item.key ? undefined : "rgba(0, 0, 0, 0.45)" }}
            >
              {item.label}
            </span>
          </Button>
        </React.Fragment>
      ))}
    </div>
  );

  const productColumns = [
    {
      title: t("product"),
      dataIndex: "name",
      render: (name, record) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 500 }}>{name}</span>
            {/* Dit is de plek waar een product opnieuw besteld wordt, dus hier hoort de
                markering het duidelijkst te zijn (BMX-49). Bestellen blijft mogelijk. */}
            <DiscontinuedMark discontinued={record.discontinued} />
          </div>
          <Num size={12} muted>
            {record.code}
            {record.color ? ` · ${getFormattedProductColor(record.color)}` : ""}
          </Num>
        </div>
      ),
    },
    {
      title: t("category"),
      dataIndex: "category",
      width: 170,
      render: (category) => <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)" }}>{getCategoryName(category)}</span>,
      sorter: (a, b) => getCategoryName(a.category).localeCompare(getCategoryName(b.category)),
    },
    {
      title: t("sold_period"),
      dataIndex: "id",
      key: "sold",
      align: "right",
      width: 110,
      render: (productId) => <Num muted={!productSalesData[productId]?.soldQuantity}>{productSalesData[productId]?.soldQuantity || 0}</Num>,
    },
    {
      title: t("stock"),
      dataIndex: "stock",
      align: "right",
      width: 100,
      sorter: (a, b) => (a.stock || 0) - (b.stock || 0),
      render: (stock) => <Num muted={!stock}>{stock || 0}</Num>,
    },
    {
      title: t("in_order"),
      dataIndex: "id",
      key: "inOrder",
      align: "right",
      width: 130,
      render: (productId) => {
        if (!purchaseOrders || shipmentsLoading) return <Spin size="small" />;
        // Zonder de order die je bewerkt, net als het voorraadbeeld in stap 2:
        // anders telt een concept zijn eigen aantallen als "al besteld".
        const orderInfo = getProductQuantityInOrders(purchaseOrders, shipments, productId, editingOrder);
        const shippedInfo = getShippedQuantityInOrders(shipments, productId);
        if (orderInfo.total === 0 && shippedInfo.total === 0) return <Num muted>—</Num>;
        return (
          <Tooltip
            title={
              <>
                {orderInfo.orders.map((o) => (
                  <div key={o.orderNumber}>
                    {o.orderNumber}: {o.amount} open
                  </div>
                ))}
                {shippedInfo.shipments.map((s) => (
                  <div key={s.shipmentNumber}>
                    {s.shipmentNumber}: {s.amount} onderweg
                  </div>
                ))}
              </>
            }
          >
            <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end" }}>
              <Num muted={!orderInfo.total}>{orderInfo.total || "—"}</Num>
              {/* Onderweg telt niet mee in "In bestelling" (dat is het nog
                  openstaande deel), maar bepaalt wel of je moet bijbestellen. */}
              {shippedInfo.total > 0 ? (
                <Num size={11.5} muted>
                  +{shippedInfo.total} onderweg
                </Num>
              ) : null}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const periodLabel =
    salesPeriod?.[0] && salesPeriod?.[1]
      ? `${salesPeriod[0].format("DD-MM-YYYY")} – ${salesPeriod[1].format("DD-MM-YYYY")}`
      : null;

  const renderLineExtras = (field) => {
    const row = addForm.getFieldValue(["products", field.name]) || {};
    const productId = row.product;
    const attachments = productAttachments[productId];

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "4px 0" }}>
        {/* Bewust geen Form.Item eromheen: de waarde wordt hier rechtstreeks in
            het formulier gezet, zodat de vlag in de regel zelf meteen meekleurt. */}
        <Select
          style={{ width: 220 }}
          value={row.priority ? "priority" : "normal"}
          onChange={(value) => {
            const rows = addForm.getFieldValue("products") || [];
            rows[field.name] = { ...rows[field.name], priority: value === "priority" };
            addForm.setFieldsValue({ products: rows });
            rerender();
          }}
          options={[
            { value: "normal", label: "Normale regel" },
            { value: "priority", label: `🔥 ${t("priority")}` },
          ]}
        />

        {row.isDraft ? (
          <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)" }}>
            {t("product_draft.supplier_fills_details")} — {t("barcode")}: {row.draftProductBarcode || "—"}
          </span>
        ) : (
          <>
            <Input.TextArea
              rows={2}
              placeholder={t("add_comments")}
              value={attachments?.comment || ""}
              onChange={(e) => {
                handleProductAttachmentToggle(productId, true);
                setProductAttachments((prev) => ({
                  ...prev,
                  [productId]: { ...prev[productId], hasAttachments: true, comment: e.target.value },
                }));
              }}
            />
            <Upload
              listType="picture"
              fileList={attachments?.fileList || []}
              onChange={async ({ fileList: newFileList }) => {
                const processed = await compressFileList(newFileList);
                setProductAttachments((prev) => ({
                  ...prev,
                  [productId]: { ...prev[productId], hasAttachments: true, fileList: processed },
                }));
              }}
              beforeUpload={() => false}
              multiple
            >
              <Button icon={<UploadOutlined />} size="small">
                {t("upload_images")}
              </Button>
            </Upload>
          </>
        )}
      </div>
    );
  };

  const aside = (
    <SectionCard title={t("summary")}>
      <KeyValueList
        items={[
          { label: t("supplier.supplier"), value: selectedSupplier?.name, wrap: true },
          { label: t("products"), value: <Num>{step === 1 ? chosenCount : lines.length}</Num> },
          { label: t("total_items"), value: <Num>{totals.units}</Num> },
          {
            label: t("total_price"),
            value: (
              <Num strong size={14}>
                {formatPriceByCurrency(totals.amount, currency)}
              </Num>
            ),
          },
        ]}
      />
    </SectionCard>
  );

  return (
    <div className="purchase-detail-page">
      {contextHolder}

      <PageHeader
        title={isEditing ? t("order.edit_purchase_order") : "Nieuwe inkooporder"}
        crumbLabel={editingOrder?.orderNumber || (isEditing ? "…" : "Nieuw")}
        meta={
          <>
            {editingOrder?.orderNumber ? <Num>{editingOrder.orderNumber}</Num> : null}
            <Tag>{t(`order.${(editingOrder?.status || PURCHASE_ORDER_STATUS_DRAFT).toLowerCase()}`)}</Tag>
          </>
        }
        extra={stepNav}
      />

      <Form form={addForm} layout="vertical" onValuesChange={rerender}>
        <DetailLayout aside={aside}>
          <SectionCard title="Bestelgegevens" padded>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: "0 20px",
              }}
            >
              <Form.Item
                name="supplier"
                label={t("supplier.supplier")}
                extra={
                  step === 1
                    ? "Bepaalt welke producten je kunt kiezen."
                    : "Een andere leverancier kies je in stap 1; dan begin je met een nieuwe productlijst."
                }
                rules={[{ required: true, message: t("please_select_supplier") }]}
              >
                <Select
                  disabled={step === 2}
                  showSearch
                  placeholder={t("select_supplier")}
                  optionFilterProp="label"
                  loading={suppliersLoading}
                  options={(suppliers || [])
                    .slice()
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((supplier) => ({ value: supplier.id, label: supplier.name }))}
                  onChange={() => {
                    // Producten hangen aan de leverancier; een andere
                    // leverancier betekent een andere lijst.
                    setSelectedProducts([]);
                    addForm.setFieldsValue({ products: [] });
                    loadSalesData(salesPeriod);
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Verkoopperiode"
                extra={
                  step === 1
                    ? "Bepaalt de verkoopcijfers in de kolom Verkocht."
                    : "Bepaalt het streepje \"verkocht\" onder elke regel."
                }
              >
                <DatePicker.RangePicker
                  value={salesPeriod}
                  onChange={(dates) => {
                    setSalesPeriod(dates);
                    loadSalesData(dates);
                  }}
                  presets={[
                    { label: "3 maanden", value: [dayjs().subtract(3, "months").startOf("day"), dayjs().endOf("day")] },
                    { label: "6 maanden", value: [dayjs().subtract(6, "months").startOf("day"), dayjs().endOf("day")] },
                    { label: "1 jaar", value: [dayjs().subtract(1, "year").startOf("day"), dayjs().endOf("day")] },
                    { label: "Dit jaar", value: [dayjs().startOf("year"), dayjs().endOf("day")] },
                  ]}
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                />
              </Form.Item>
            </div>
          </SectionCard>

          {step === 1 ? (
            <>
              <SectionCard
                title={t("products")}
                meta={chosenCount ? `${chosenCount} gekozen` : null}
                extra={
                  <Space wrap>
                    <Input
                      placeholder="Zoek op naam of code"
                      prefix={<SearchOutlined />}
                      value={productSearchText}
                      onChange={(e) => setProductSearchText(e.target.value)}
                      style={{ width: 220 }}
                      allowClear
                    />
                    <Button
                      icon={<FileAddOutlined />}
                      disabled={!selectedSupplierId}
                      onClick={() => setAddDraftProductModalOpen(true)}
                    >
                      {t("product_draft.add_draft_product")}
                    </Button>
                  </Space>
                }
                footer={
                  draftProducts.length > 0 ? (
                    <>
                      <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)" }}>
                        {t("product_draft.new_product")}:
                      </span>
                      {draftProducts.map((draft) => (
                        <Tag
                          key={draft.tempId}
                          closable
                          onClose={() => setDraftProducts((prev) => prev.filter((d) => d.tempId !== draft.tempId))}
                        >
                          {draft.productName} ({draft.productCode})
                        </Tag>
                      ))}
                    </>
                  ) : null
                }
              >
                {!selectedSupplierId ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Kies eerst een leverancier"
                    style={{ padding: "32px 0" }}
                  />
                ) : !selectedSupplier?.productIds?.length ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Aan deze leverancier zijn nog geen producten gekoppeld"
                    style={{ padding: "32px 0" }}
                  >
                    <Button type="primary" onClick={() => navigate(urls.suppliersList)}>
                      Naar leveranciers
                    </Button>
                  </Empty>
                ) : (
                  <Table
                    dataSource={getSupplierProducts()}
                    rowKey="id"
                    size="middle"
                    scroll={{ x: 820 }}
                    rowSelection={{
                      selectedRowKeys: selectedProducts,
                      preserveSelectedRowKeys: true,
                      onChange: setSelectedProducts,
                    }}
                    columns={productColumns}
                    pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "25", "50", "100"] }}
                  />
                )}
              </SectionCard>
            </>
          ) : (
            <>
              <Form.List name="products">
                {(fields, { remove }) => (
                  <SectionCard
                    title="Regels"
                    meta={`${fields.length} regel${fields.length === 1 ? "" : "s"} · ${totals.units} stuks`}
                    extra={<LineStockLegend />}
                    footer={
                      <Button type="dashed" icon={<PlusOutlined />} onClick={() => setStep(1)}>
                        Product toevoegen
                      </Button>
                    }
                  >
                    <Table
                      dataSource={fields}
                      rowKey="key"
                      size="middle"
                      pagination={false}
                      scroll={{ x: 900 }}
                      expandable={{ expandedRowRender: renderLineExtras }}
                      columns={[
                        {
                          title: t("product"),
                          key: "product",
                          render: (_, field) => {
                            const row = addForm.getFieldValue(["products", field.name]) || {};
                            const product = (products || []).find((p) => p.id === row.product);
                            return (
                              <div style={{ minWidth: 0 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ fontWeight: 500 }}>
                                    {row.isDraft ? row.draftProductName : product?.name || t("select_a_product")}
                                  </span>
                                  {row.isDraft ? <Tag color="gold">{t("product_draft.new_product")}</Tag> : null}
                                  {row.priority ? <Tag color="red">🔥</Tag> : null}
                                </div>
                                <Num size={12} muted>
                                  {row.isDraft ? row.draftProductCode : product?.code}
                                  {!row.isDraft && product?.color ? ` · ${getFormattedProductColor(product.color)}` : ""}
                                </Num>
                                {/* Een productconcept bestaat nog niet in de catalogus:
                                    geen voorraad, geen verkoop, geen eerdere orders. */}
                                {!row.isDraft && product ? (
                                  <LineStockPicture
                                    loading={!purchaseOrders || shipmentsLoading || salesLoading}
                                    periodLabel={periodLabel}
                                    picture={getLineStockPicture({
                                      product,
                                      purchaseOrders,
                                      shipments,
                                      editingOrder,
                                      soldQuantity: productSalesData[product.id]?.soldQuantity,
                                      amount: row.amount,
                                    })}
                                  />
                                ) : null}
                              </div>
                            );
                          },
                        },
                        {
                          title: "Aantal",
                          key: "amount",
                          align: "right",
                          width: 110,
                          render: (_, field) => (
                            <Form.Item name={[field.name, "amount"]} noStyle>
                              <InputNumber
                                min={0}
                                placeholder="0"
                                controls={false}
                                className="purchase-num-input"
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                          ),
                        },
                        {
                          title: "Inner",
                          key: "inner",
                          align: "right",
                          width: 100,
                          render: (_, field) => {
                            const row = addForm.getFieldValue(["products", field.name]) || {};
                            if (row.isDraft) return <Num muted>—</Num>;
                            return (
                              <Form.Item name={[field.name, "innerPackagingQuantity"]} noStyle>
                                <InputNumber
                                  min={0}
                                  placeholder="0"
                                  controls={false}
                                  className="purchase-num-input"
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            );
                          },
                        },
                        {
                          title: "Master",
                          key: "master",
                          align: "right",
                          width: 100,
                          render: (_, field) => {
                            const row = addForm.getFieldValue(["products", field.name]) || {};
                            if (row.isDraft) return <Num muted>—</Num>;
                            return (
                              <Form.Item name={[field.name, "masterPackagingQuantity"]} noStyle>
                                <InputNumber
                                  min={0}
                                  placeholder="0"
                                  controls={false}
                                  className="purchase-num-input"
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            );
                          },
                        },
                        {
                          title: t("price_per_unit"),
                          key: "price",
                          align: "right",
                          width: 140,
                          render: (_, field) => {
                            const row = addForm.getFieldValue(["products", field.name]) || {};
                            if (row.isDraft) return <Num muted>—</Num>;
                            return (
                              <Form.Item name={[field.name, "price"]} noStyle>
                                <InputNumber
                                  min={0}
                                  step={0.01}
                                  precision={2}
                                  placeholder="0.00"
                                  controls={false}
                                  className="purchase-num-input"
                                  prefix={currency === "USD" ? "$" : currency}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            );
                          },
                        },
                        {
                          title: "Subtotaal",
                          key: "subtotal",
                          align: "right",
                          width: 130,
                          render: (_, field) => {
                            const row = addForm.getFieldValue(["products", field.name]) || {};
                            const subtotal = (Number(row.amount) || 0) * (Number(row.price) || 0);
                            return <Num muted={!subtotal}>{formatPriceByCurrency(subtotal, currency)}</Num>;
                          },
                        },
                        {
                          title: "",
                          key: "remove",
                          width: 50,
                          render: (_, field) => (
                            <Button
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => removeLine(field, remove)}
                              title="Regel verwijderen"
                            />
                          ),
                        },
                      ]}
                      summary={() =>
                        fields.length > 1 ? (
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={2}>
                              <span style={{ fontWeight: 500 }}>Totaal</span>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="right">
                              <Num strong>{totals.units}</Num>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} colSpan={3} />
                            <Table.Summary.Cell index={6} align="right">
                              <Num strong>{formatPriceByCurrency(totals.amount, currency)}</Num>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={7} />
                          </Table.Summary.Row>
                        ) : null
                      }
                    />
                  </SectionCard>
                )}
              </Form.List>

              <SectionCard title="Opmerking en bijlagen" padded>
                <Form.Item name="comments" label={t("comments")} style={{ marginBottom: 16 }}>
                  <Input.TextArea rows={3} placeholder="Opmerking voor de leverancier (optioneel)" />
                </Form.Item>
                <Form.Item label={t("images")} style={{ marginBottom: 0 }}>
                  <Upload
                    listType="picture"
                    fileList={fileList}
                    onChange={async ({ fileList: newFileList }) => setFileList(await compressFileList(newFileList))}
                    beforeUpload={() => false}
                    multiple
                  >
                    <Button icon={<UploadOutlined />}>{t("upload_images")}</Button>
                  </Upload>
                </Form.Item>
              </SectionCard>
            </>
          )}
        </DetailLayout>
      </Form>

      <StickyActionBar note={actionBarNote}>
        <Button onClick={() => navigate(urls.purchaseOrdersList)}>{t("cancel")}</Button>
        <Button onClick={() => handleSave()} loading={isSubmitting}>
          Opslaan als concept
        </Button>
        {step === 1 ? (
          <Button type="primary" onClick={goToStep2}>
            {t("next")}
          </Button>
        ) : (
          <Tooltip title={canSendToSupplier ? null : t("draft_products_incomplete")}>
            {/* De span is nodig: een disabled knop geeft geen muis-events door,
                dus zonder wrapper blijft de uitleg onzichtbaar. */}
            <span>
              <Button
                type="primary"
                disabled={!canSendToSupplier}
                onClick={confirmSendToSupplier}
                loading={isSubmitting}
              >
                {t("save_and_send_supplier")}
              </Button>
            </span>
          </Tooltip>
        )}
      </StickyActionBar>

      <Modal
        title={t("product_draft.add_draft_product")}
        open={addDraftProductModalOpen}
        onCancel={() => {
          setAddDraftProductModalOpen(false);
          draftProductForm.resetFields();
        }}
        onOk={handleAddDraftProduct}
        okText={t("add")}
      >
        <Form form={draftProductForm} layout="vertical">
          <Form.Item
            name="productName"
            label={t("product_draft.draft_product_name")}
            rules={[{ required: true, message: t("form.enter_value") }]}
          >
            <Input placeholder="Bijv. Amazez Large" />
          </Form.Item>
          <Form.Item
            name="productCode"
            label={t("product_draft.draft_product_code")}
            rules={[{ required: true, message: t("form.enter_value") }]}
          >
            <Input placeholder="Bijv. BG10000" />
          </Form.Item>
          <Form.Item name="productBarcode" label={t("product_draft.draft_product_barcode")}>
            <Input placeholder="Bijv. 8712345678901" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PurchaseOrderCreate;
